import { normalizePhone } from "@commerce-os/utils";
import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { paisa } from "./money.js";
/** Tenant settings (docs/specs/tenant-settings.md, FND-13): the business profile and switches other modules read. */
export const TAX_MODES = ["inclusive", "exclusive"];
export const taxModeSchema = z.enum(TAX_MODES);
export const customerLocaleSchema = z.enum(["en", "bn"]);
/**
 * Any IANA zone the runtime accepts (`Asia/Dhaka`, `UTC`, and aliases such as `Asia/Kolkata`, which
 * `Intl.supportedValuesOf` lists under its older name `Asia/Calcutta`).
 */
export const timeZoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine((zone) => {
    try {
        new Intl.DateTimeFormat("en", { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
}, "Unknown time zone");
/** A Bangladeshi mobile number as typed; stored as E.164 (CLAUDE.md §7). */
export const businessPhoneSchema = z
    .string()
    .trim()
    .max(32)
    .refine((phone) => normalizePhone(phone) !== null, "Not a Bangladeshi mobile number");
/** Business Identification Number (NBR e-BIN): 13 digits, the VAT registration number. */
export const binSchema = z.string().regex(/^\d{13}$/);
const settingsText = z.string().trim().max(500);
export const tenantSettingsSchema = z.object({
    name: z.string(),
    logo: z.object({ fileId: z.uuid(), url: z.string(), thumbUrl: z.string().nullable() }).nullable(),
    address: z.string(),
    phone: z.string().nullable(),
    bin: z.string().nullable(),
    timezone: z.string(),
    invoiceFooter: z.string(),
    customerLocale: customerLocaleSchema,
    taxMode: taxModeSchema,
    /** "Limit" discounts above this (basis points: 500 = 5 %) need a manager PIN. */
    discountLimitBp: z.int(),
    /** "Limit" refunds above this amount need a manager PIN. */
    refundLimitPaisa: paisa(),
    /** Optimistic lock for the whole form; 0 until the first save. */
    version: z.int().min(0),
});
/** `PATCH /v1/settings`: only the fields that change, plus the `version` last read (409 if it moved on). */
export const updateTenantSettingsSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    logoFileId: z.uuid().nullable().optional(),
    address: settingsText.optional(),
    phone: businessPhoneSchema.nullable().optional(),
    bin: binSchema.nullable().optional(),
    timezone: timeZoneSchema.optional(),
    invoiceFooter: settingsText.optional(),
    customerLocale: customerLocaleSchema.optional(),
    taxMode: taxModeSchema.optional(),
    discountLimitBp: z.int().min(0).max(10_000).optional(),
    refundLimitPaisa: paisa().min(0).optional(),
    version: z.int().min(0),
})
    .strict()
    .refine((change) => Object.keys(change).some((key) => key !== "version"), "Nothing to change");
const settings = { tag: "settings" };
export const getTenantSettings = defineEndpoint({
    ...settings,
    name: "getTenantSettings",
    method: "GET",
    path: "/v1/settings",
    access: { permission: "settings.view" },
    summary: "The business profile, regional settings and sales thresholds",
    status: 200,
    response: tenantSettingsSchema,
});
export const updateTenantSettings = defineEndpoint({
    ...settings,
    name: "updateTenantSettings",
    method: "PATCH",
    path: "/v1/settings",
    access: { permission: "settings.manage" },
    summary: "Change tenant settings (audited with before/after)",
    body: updateTenantSettingsSchema,
    status: 200,
    response: tenantSettingsSchema,
    errors: ["CONFLICT", "FILE_REJECTED"],
});
//# sourceMappingURL=settings.js.map